import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  AlertBannerContext,
  CUSTOMER_CREATE_MODAL_CONFLICT_CODES,
  HandledGraphQLMutationError
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  createCustomerQuery,
  listCustomersQuery,
  getCustomerDetails,
  updateCustomerQuery,
  deleteCustomerQuery
} from "../../Graphql/querries/customerQuery";
import {
  setCustomerList,
  addCustomer,
  updateCustomer,
  deleteCustomer
} from "../../store/slices/customerSlice";
import { Customer } from "../../components/Customers/types/customer";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE
} from "@kivunova/kivufrontendcommon";

import { RootState } from "../../store/store";
import {
  ListCustomersResponse,
  GetCustomerInput,
  GetCustomerResponse,
  CreateCustomerInput,
  CreateCustomerResponse,
  UpdateCustomerInput,
  UpdateCustomerResponse,
  DeleteCustomerInput,
  DeleteCustomerResponse
} from "../../components/Customers/types/customer";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

/**
 * useGetCustomerList
 * Fetch list of customers for a company
 */
export const useGetCustomerList = (
  companyId?: string,
  listStoreId?: string | null
) => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useQuery<Customer[], Error>({
    queryKey: ["customerList", companyId, listStoreId ?? null],
    queryFn: async () => {
      const userId = userDetails?.id;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching customer list",
        buildLogContext(
          "customers",
          "get-customer-list",
          userId,
          companyId,
          storeId
        )
      );

      try {
        const listInput: { companyId: string; storeId?: string } = {
          companyId: companyId!
        };
        const trimmed = listStoreId?.trim();
        if (trimmed) {
          listInput.storeId = trimmed;
        }
        const response: ListCustomersResponse = await fetchGraphQL(
          listCustomersQuery,
          { input: listInput },
          setAlert
        );

        const { customers, code } = response.listCustomers;

        /**
         * 🟡 Business warning: CUSTOMER_TIN_EXISTS
         * - Data is valid
         * - UI should warn user
         * - Still continue normally
         */
        if (code === API_RESPONSE_CODE.CUSTOMER_TIN_EXISTS) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Customer TIN already exists",
            buildLogContext(
              "customers",
              "get-customer-list",
              userId,
              companyId,
              storeId,
              { apiCode: String(code) }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.WARNING,
            code: ALERT_BANNER_CODE.CUSTOMER_TIN_EXISTS
          });

          if (customers?.length) {
            dispatch(setCustomerList(customers));
            return customers;
          }

          return [];
        }

        // ⚠️ Expected API/business failure
        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch customer list",
            buildLogContext(
              "customers",
              "get-customer-list",
              userId,
              companyId,
              storeId,
              { apiCode: String(code) }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });

          return [];
        }

        // ❌ Logically broken success response
        if (!customers) {
          captureAppException(
            "API returned OK but customers data missing",
            new Error("API returned OK but customers data missing"),
            buildLogContext(
              "customers",
              "get-customer-list",
              userId,
              companyId,
              storeId
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });

          return [];
        }

        // ✅ Success
        dispatch(setCustomerList(customers));

        sendAppLog(
          LOG_LEVEL.INFO,
          "Customer list fetched successfully",
          buildLogContext(
            "customers",
            "get-customer-list",
            userId,
            companyId,
            storeId,
            { customerCount: customers.length }
          )
        );

        return customers;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to fetch customer list",
          error,
          buildLogContext(
            "customers",
            "get-customer-list",
            userId,
            companyId,
            storeId
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        return [];
      }
    },
    enabled: !!companyId
  });
};

/**
 * useGetCustomerDetails
 * Fetch details for a specific customer
 */
export const useGetCustomerDetails = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Customer | null, Error, GetCustomerInput>({
    mutationKey: ["customerDetails"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching customer details",
        buildLogContext(
          "customers",
          "get-customer-details",
          userId,
          // companyId not available on GetCustomerInput — customer is looked up by customerId
          undefined,
          storeId,
          { customerId: String(input.customerId) }
        )
      );

      try {
        const response: GetCustomerResponse = await fetchGraphQL(
          getCustomerDetails,
          { input },
          setAlert
        );

        const { customer, code } = response.getCustomer;

        // ⚠️ Expected API/business failure
        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch customer details",
            buildLogContext(
              "customers",
              "get-customer-details",
              userId,
              undefined, // companyId not available on GetCustomerInput
              storeId,
              { apiCode: String(code), customerId: String(input.customerId) }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        // ❌ Logically broken success response
        if (!customer) {
          captureAppException(
            "API returned OK but customer data missing",
            new Error("API returned OK but customer data missing"),
            buildLogContext(
              "customers",
              "get-customer-details",
              userId,
              undefined, // companyId not available on GetCustomerInput
              storeId,
              { customerId: String(input.customerId) }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Customer details fetched successfully",
          buildLogContext(
            "customers",
            "get-customer-details",
            userId,
            undefined, // companyId not available on GetCustomerInput
            storeId,
            { customerId: String(input.customerId) }
          )
        );

        return customer;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to fetch customer details",
          error,
          buildLogContext(
            "customers",
            "get-customer-details",
            userId,
            undefined, // companyId not available on GetCustomerInput
            storeId,
            { customerId: String(input.customerId) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        return null;
      }
    }
  });
};

/**
 * useCreateCustomer
 * Create a new customer
 */
export const useCreateCustomer = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<CreateCustomerResponse, Error, CreateCustomerInput>({
    mutationKey: ["createCustomer"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating customer",
        buildLogContext(
          "customers",
          "create-customer",
          userId,
          // companyId not on CreateCustomerInput directly; comes from the user's session context
          undefined,
          storeId
        )
      );

      const response: CreateCustomerResponse = await fetchGraphQL(
        createCustomerQuery,
        { input },
        setAlert
      );
      const createResult = response?.createCustomer;

      // ❌ Unexpected response shape
      if (!createResult) {
        captureAppException(
          "Create customer failed: missing createCustomer",
          new Error("Create customer failed: missing createCustomer"),
          buildLogContext(
            "customers",
            "create-customer",
            userId,
            undefined, // companyId not on CreateCustomerInput
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Create customer: missing createCustomer payload"
        );
      }

      // ⚠️ Expected API/business failure
      if (createResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Customer creation rejected by API",
          buildLogContext(
            "customers",
            "create-customer",
            userId,
            undefined, // companyId not on CreateCustomerInput
            storeId,
            { apiCode: String(createResult.code) }
          )
        );
        if (!CUSTOMER_CREATE_MODAL_CONFLICT_CODES.has(createResult.code)) {
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: createResult.code as unknown as ALERT_BANNER_CODE
          });
        }
        throw new HandledGraphQLMutationError(
          `Customer creation rejected by API: ${createResult.code}`,
          createResult.code
        );
      }

      const newCustomer = createResult.customer;

      // ❌ Logically broken success response
      if (!newCustomer) {
        captureAppException(
          "API returned OK but customer data missing",
          new Error(
            "API returned OK but customer data missing after createCustomer"
          ),
          buildLogContext(
            "customers",
            "create-customer",
            userId,
            undefined, // companyId not on CreateCustomerInput
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "API returned OK but customer data missing after createCustomer"
        );
      }

      return response;
    },

    onSuccess: (response) => {
      const userId = userDetails?.id;
      const storeId = userDetails?.storeId;
      const newCustomer = response.createCustomer!.customer!;

      dispatch(addCustomer(newCustomer));

      // Keep react-query consumers (e.g. sales-order customer pickers) in sync
      queryClient.invalidateQueries({ queryKey: ["customerList"] });

      sendAppLog(
        LOG_LEVEL.INFO,
        "Customer created successfully",
        buildLogContext(
          "customers",
          "create-customer",
          userId,
          undefined, // companyId not available in onSuccess — customer object does not carry it
          storeId,
          { customerId: String(newCustomer.id) }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.CUSTOMER_CREATED_SUCCESSFULLY
      });
    },

    onError: (error) => {
      if (error instanceof HandledGraphQLMutationError) {
        return;
      }

      const userId = userDetails?.id;
      const storeId = userDetails?.storeId;

      // ❌ Unexpected runtime/network error
      captureAppException(
        "Failed to create customer",
        error,
        buildLogContext(
          "customers",
          "create-customer",
          userId,
          undefined, // companyId not available in onError scope
          storeId
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

/**
 * useUpdateCustomer
 * Update existing customer information
 */
export const useUpdateCustomer = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<UpdateCustomerResponse, Error, UpdateCustomerInput>({
    mutationKey: ["updateCustomer"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started updating customer",
        buildLogContext(
          "customers",
          "update-customer",
          userId,
          undefined, // companyId not on UpdateCustomerInput
          storeId,
          { customerId: String(input.customerId) }
        )
      );

      return fetchGraphQL(updateCustomerQuery, { input }, setAlert);
    },

    onSuccess: (response, input) => {
      const userId = userDetails?.id;
      const storeId = userDetails?.storeId;
      const updateResult = response?.updateCustomer;

      // ❌ Unexpected response shape
      if (!updateResult) {
        captureAppException(
          "Update customer failed: missing updateCustomer",
          new Error("Update customer failed: missing updateCustomer"),
          buildLogContext(
            "customers",
            "update-customer",
            userId,
            undefined, // companyId not on UpdateCustomerInput
            storeId,
            { customerId: String(input.customerId) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return;
      }

      // ⚠️ Expected API/business failure
      if (updateResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Customer update rejected by API",
          buildLogContext(
            "customers",
            "update-customer",
            userId,
            undefined, // companyId not on UpdateCustomerInput
            storeId,
            {
              apiCode: String(updateResult.code),
              customerId: String(input.customerId)
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: updateResult.code as unknown as ALERT_BANNER_CODE
        });
        return;
      }

      const updatedCustomer = updateResult.customer;

      // ❌ Logically broken success response
      if (!updatedCustomer) {
        captureAppException(
          "API returned OK but customer data missing",
          new Error("API returned OK but customer data missing"),
          buildLogContext(
            "customers",
            "update-customer",
            userId,
            undefined, // companyId not on UpdateCustomerInput
            storeId,
            { customerId: String(input.customerId) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return;
      }

      // ✅ Success
      dispatch(updateCustomer(updatedCustomer));

      sendAppLog(
        LOG_LEVEL.INFO,
        "Customer updated successfully",
        buildLogContext(
          "customers",
          "update-customer",
          userId,
          undefined, // companyId not on UpdateCustomerInput
          storeId,
          { customerId: String(input.customerId) }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.CUSTOMER_UPDATED_SUCCESSFULLY
      });
    },

    onError: (error, input) => {
      const userId = userDetails?.id;
      const storeId = userDetails?.storeId;

      // ❌ Unexpected runtime/network error
      captureAppException(
        "Failed to update customer",
        error,
        buildLogContext(
          "customers",
          "update-customer",
          userId,
          undefined, // companyId not on UpdateCustomerInput
          storeId,
          { customerId: String(input?.customerId) }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

/**
 * useDeleteCustomer
 * Delete a customer
 */
export const useDeleteCustomer = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<DeleteCustomerResponse, Error, DeleteCustomerInput>({
    mutationKey: ["deleteCustomer"],

    mutationFn: async (input) => {
      console.log("🟡 mutationFn input:", input, typeof input);
      const userId = userDetails?.id;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started deleting customer",
        buildLogContext(
          "customers",
          "delete-customer",
          userId,
          undefined, // companyId not on DeleteCustomerInput
          storeId,
          { customerId: String(input.customerId) }
        )
      );

      return fetchGraphQL(deleteCustomerQuery, { input }, setAlert);
    },

    onSuccess: (response, input) => {
      const userId = userDetails?.id;
      const storeId = userDetails?.storeId;
      const deleteResult = response?.deleteCustomer;

      // ❌ Unexpected response shape
      if (!deleteResult) {
        captureAppException(
          "Delete customer failed: missing deleteCustomer",
          new Error("Delete customer failed: missing deleteCustomer"),
          buildLogContext(
            "customers",
            "delete-customer",
            userId,
            undefined, // companyId not on DeleteCustomerInput
            storeId,
            { customerId: String(input.customerId) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return;
      }

      // ⚠️ Expected API/business failure
      if (deleteResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Customer deletion rejected by API",
          buildLogContext(
            "customers",
            "delete-customer",
            userId,
            undefined, // companyId not on DeleteCustomerInput
            storeId,
            {
              apiCode: String(deleteResult.code),
              customerId: String(input.customerId)
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: deleteResult.code as unknown as ALERT_BANNER_CODE
        });
        return;
      }

      // ✅ Success
      dispatch(deleteCustomer(input.customerId));

      sendAppLog(
        LOG_LEVEL.INFO,
        "Customer deleted successfully",
        buildLogContext(
          "customers",
          "delete-customer",
          userId,
          undefined, // companyId not on DeleteCustomerInput
          storeId,
          { customerId: String(input.customerId) }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.CUSTOMER_DELETED_SUCCESSFULLY
      });
    },

    onError: (error, input) => {
      const userId = userDetails?.id;
      const storeId = userDetails?.storeId;

      // ❌ Unexpected runtime/network error
      captureAppException(
        "Failed to delete customer",
        error,
        buildLogContext(
          "customers",
          "delete-customer",
          userId,
          undefined, // companyId not on DeleteCustomerInput
          storeId,
          { customerId: String(input?.customerId) }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};
